/**
 * Turning recogniser output and LLM suggestions into something with letters on it.
 *
 * The recogniser has no opinion about spelling and the LLM's opinion is not authoritative
 * (`README`, rule 3), so both are re-spelled here against the running key estimate. That
 * is why the same sonority reads "E♭7" in a flat context and "D♯7" in B major without
 * anything being re-detected or re-generated.
 */

import {
  chordToneNames,
  mod12,
  noteName,
  respell,
  spellOffered,
  spellPitchClass,
  toCanonical,
  type BranchSuggestion,
  type CanonicalChord,
  type ChordHypothesis,
  type ChordSpec,
  type DetectedChord,
  type Extension,
  type KeyContext,
  type PitchClass,
} from '@hc/shared';

/**
 * Which extension a note sitting this many semitones above the root represents.
 *
 * Nine semitones is both a sixth and a thirteenth; the seventh decides which, because
 * "C13" without a seventh is not a thing anyone writes.
 */
const EXTENSION_BY_SEMITONE: Readonly<Record<number, Extension>> = Object.freeze({
  1: 'b9',
  2: '9',
  3: '#9',
  5: '11',
  6: '#11',
  8: 'b13',
  9: '13',
});

/**
 * A recogniser hypothesis as a spelled, canonical chord in the current key.
 *
 * `ChordHypothesis` describes its chord twice and unequally: `quality` and `seventh`
 * cover the triad and the seventh, but `pitchClasses` is the real content and can carry
 * notes those two fields have no vocabulary for. A C6 and a C triad have the same quality
 * and the same (absent) seventh — they differ only in the pitch-class set. So the set is
 * treated as authoritative and any note the triad-plus-seventh does not account for is
 * mapped back to the extension it must be. Without this, every added sixth and ninth in
 * the correction sheet silently collapses into a plain triad.
 */
export function hypothesisToChord(hypothesis: ChordHypothesis, key: KeyContext): CanonicalChord {
  const spec: ChordSpec = {
    root: spellPitchClass(hypothesis.rootPc, key),
    quality: hypothesis.quality,
    extensions: [],
    alterations: [],
    omissions: [],
  };
  if (hypothesis.seventh) spec.seventh = hypothesis.seventh;

  const base = toCanonical(spec);
  const accountedFor = new Set(base.pitchClasses);
  for (const pc of hypothesis.pitchClasses) {
    if (accountedFor.has(pc)) continue;
    const semitone = mod12(pc - hypothesis.rootPc);
    const extension = semitone === 9 && !hypothesis.seventh ? '6' : EXTENSION_BY_SEMITONE[semitone];
    if (extension && !spec.extensions.includes(extension)) spec.extensions.push(extension);
  }

  // The recogniser reports the bass separately, so an inversion survives into the symbol.
  if (hypothesis.bassPc !== hypothesis.rootPc) {
    spec.bass = spellPitchClass(hypothesis.bassPc, key);
  }
  // `respell` gets the final word: it knows how to keep a slash bass inside the chord's
  // own stack of thirds (Cm7/E♭, never Cm7/D♯).
  return respell(toCanonical(spec), key);
}

/**
 * The suggestion's chord, spelled for the key the player is actually in.
 *
 * Through `spellOffered`, because this is a chord being *offered* and the built-in engine
 * spells its own targets exactly that way. With `respell` alone the card and the engine
 * disagreed: from a lone Am, `respell` names the leading-tone diminished seventh A♭dim7 —
 * A♭–C♭–E𝄫–G𝄫 — while the engine wrote G♯dim7 and computed its numeral, ♯vii°7, from the
 * G♯, so the card printed a numeral for a letter it was not showing. The suggestion's own
 * root goes in as the intended spelling, which is what lets a C♯ the engine chose as "a
 * major third above A" survive arrival rather than being flattened back to D♭; the key
 * still overrules it wherever it would cost a double accidental or more than one extra
 * accidental, so a model's odd spelling is not simply trusted.
 */
export function suggestionToChord(suggestion: BranchSuggestion, key: KeyContext): CanonicalChord {
  return spellOffered(toCanonical(suggestion.chord), key, suggestion.chord.root);
}

/**
 * The chord's notes, root up and spelled as the chord's own stack. "E · G♯ · B · D".
 *
 * Letters come from the chord's root, not from spelling each pitch class against the key
 * on its own. The key is still the authority — every chord that reaches here has already
 * had its root chosen against the key by `respell` — but a stack of thirds then has
 * exactly one spelling, and spelling note by note broke it: Amaj7 under a C-ish estimate
 * read "D♭ · E · A♭ · A", four letters nobody would write for that chord, in an order
 * (ascending from C, which is merely how `pitchClasses` is stored) that hides the root.
 * Not a suggestion's own `noteSpellings` either, for the reason at the top of this file.
 *
 * A slash chord reads with its bass in front and the stack above it — "E · C · G" for
 * C/E, "D · C · E · G" for C/D — which is the order the chord sounds in, the order the
 * engine and the server's validator write `noteSpellings` in (`chordToneNames`), and
 * the note the keyboard diagram marks as the bass. Where a chord has no stack of thirds
 * to read (`chordToneNames` returns null), or the stack somehow does not account for
 * every sounding pitch class, it falls back to the old reading: ascending from C, each
 * note spelled against the key. Four callers want the same string — the current node's
 * two readings, an expanded branch card, and the history detail — and a keyboard diagram
 * beside note names that disagree with it is worse than either alone.
 */
export function pitchClassNames(chord: CanonicalChord, key: KeyContext): string {
  const names = chordToneNames(chord);
  // One name per sounding pitch class, or the stack is describing a different chord from
  // the one the keyboard diagram is drawing — and then the plain reading is the honest one.
  if (names && names.length === chord.pitchClasses.length) return names.join(' · ');
  return chord.pitchClasses.map((pc) => noteName(spellPitchClass(pc, key))).join(' · ');
}

/** The top alternatives a correction sheet should offer, best first, at most three. */
export function correctionCandidates(
  detected: DetectedChord | undefined,
  key: KeyContext,
): Array<{ hypothesis: ChordHypothesis; chord: CanonicalChord }> {
  if (!detected) return [];
  return detected.alternatives.slice(0, 3).map((hypothesis) => ({
    hypothesis,
    chord: hypothesisToChord(hypothesis, key),
  }));
}

/**
 * A `DetectedChord` for a chord that was typed rather than heard.
 *
 * `CHORD_DETECTED` is the only action in the frozen contract that appends a node, so
 * manual entry has to speak the recogniser's language. The chroma is a clean one-hot per
 * pitch class rather than fabricated spectral data: a later correction re-scored against
 * it will simply find the chord that was typed, which is the honest answer.
 */
export function synthesizeDetection(chord: CanonicalChord, timestamp: number): DetectedChord {
  const chroma = new Array<number>(12).fill(0);
  for (const pc of chord.pitchClasses) chroma[pc] = 1;

  const best: ChordHypothesis = {
    pitchClasses: [...chord.pitchClasses] as PitchClass[],
    rootPc: chord.rootPc,
    bassPc: chord.bassPc,
    quality: chord.quality,
    score: 1,
  };
  if (chord.seventh) best.seventh = chord.seventh;

  return {
    best,
    alternatives: [],
    chroma,
    confidence: 1,
    timestamp,
    onsetTimestamp: timestamp,
  };
}
